import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { CITIES } from "@/data/cities";

/**
 * Section éditoriale de maillage interne vers les pages locales.
 * Utilisée sur /mariage, /photobooth, /anniversaire, /entreprises.
 *
 * Props:
 *   - eyebrow: petite étiquette au-dessus du titre
 *   - title: titre principal (peut inclure du markup)
 *   - urlPrefix: "dj-mariage" | "photobooth" | "anniversaire" | "entreprise"
 *   - intro: paragraphe éditorial contextualisé
 *   - citySlugs: liste blanche des slugs à afficher (par ex. les 10 villes prioritaires)
 *   - testidPrefix: préfixe des data-testid
 */
export default function NearbyCitiesLinks({
  eyebrow = "Votre DJ près de chez vous",
  title,
  urlPrefix,
  intro,
  citySlugs,
  testidPrefix = "nearby-city",
}) {
  const cities = (citySlugs || CITIES.map((c) => c.slug))
    .map((slug) => CITIES.find((c) => c.slug === slug))
    .filter((c) => c && (!c.mariage_only || urlPrefix === "dj-mariage"));

  if (!cities.length || !urlPrefix) return null;

  return (
    <section
      className="py-20 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1200px] mx-auto"
      data-testid={`${testidPrefix}-section`}
    >
      <div className="mb-10 reveal">
        <span className="eyebrow">{eyebrow}</span>
        {title && (
          <h2 className="font-serif-luxe text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.05]">
            {title}
          </h2>
        )}
        {intro && (
          <p className="text-white/65 text-base leading-relaxed mt-5 max-w-3xl">{intro}</p>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {cities.map((c) => (
          <Link
            key={c.slug}
            to={`/${urlPrefix}-${c.slug}`}
            data-testid={`${testidPrefix}-${c.slug}`}
            className="group border border-[#DCB457]/20 hover:border-[#DCB457]/60 bg-[#0a0a0a]/40 hover:bg-[#DCB457]/5 px-4 py-3.5 transition-colors"
          >
            <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/45 group-hover:text-[#DCB457]/80">
              <MapPin size={11} strokeWidth={1.4} /> {c.cp}
            </span>
            <span className="block font-serif-luxe text-lg mt-1.5 text-white group-hover:text-[#DCB457] transition-colors">
              {c.name}
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] text-white/40 mt-1">
              Voir la page <ArrowRight size={10} strokeWidth={1.5} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
